// App.js
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  createBrowserRouter,
  RouterProvider,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { Box } from '@mui/material';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import Login from './pages/shared/Login';
import UserRegistration from './pages/admin/UserRegistration';
import UserList from './pages/admin/UserList';
import UserEdit from './pages/admin/UserEdit';
import UserDetails from './pages/admin/UserDetails';
import HorseRegistration from './pages/admin/HorseRegistration';
import Dashboard from './pages/admin/Dashboard';
import HorseList from './pages/admin/HorseList';
import HorseEdit from './pages/admin/HorseEdit';
import HorseCompetitionsPage from './pages/admin/HorseCompetitionsPage';
import Statistics from './pages/admin/Statistics';
import SystemSettings from './pages/admin/SystemSettings';
import AccountantReview from './pages/admin/AccountantReview';
import ExpensesPage from './pages/admin/ExpensesPage';
import HorseDetails from './pages/shared/HorseDetails';
import HorseDetailsClient from './pages/shared/HorseDetailsClient';
import ClientHorseDetails from './pages/shared/ClientHorseDetails';
import ClientHorseList from './pages/shared/ClientHorseList';
import ClientHorseStats from './pages/shared/ClientHorseStats';
import PedigreeTree from './pages/shared/PedigreeTree';
import OwnerList from './pages/admin/OwnerList';
import OwnerHorsesList from './pages/admin/OwnerHorsesList';
import OwnerEdit from './pages/admin/OwnerEdit';
import OwnerRegistration from './pages/admin/OwnerRegistration';
import OwnerDetails from './pages/admin/OwnerDetails';
import StableList from './pages/admin/StableList';
import StableDetails from './pages/admin/StableDetails';
import StableRoomList from './pages/admin/StableRoomList';
import StableRoomDetails from './pages/admin/StableRoomDetails';
import AddStable from './pages/admin/AddStable';
import AccommodationList from './pages/admin/AccommodationList';
import AvailableRoomsView from './pages/admin/AvailableRoomsView';
import HorseColorList from './pages/admin/HorseColorList';
import HorseBreedList from './pages/admin/HorseBreedList';
import RoleList from './pages/admin/RoleList';
import HorseTransportForm from './pages/admin/HorseTransportForm';
import HorseTransportReportPage from './pages/admin/HorseTransportReportPage';
import ProceduresList from './pages/clinic/ProceduresList';
import RecordList from './pages/clinic/RecordList';
import MedicationsList from './pages/clinic/MedicationsList';
import ProcedureAdd from './pages/clinic/ProcedureAdd';
import MedicationAdd from './pages/clinic/MedicationAdd';
import HorseHistory from './pages/clinic/HorseHistory';
import MedicationsEdit from './pages/clinic/MedicationsEdit';
import VetClinicRecordInside from './pages/clinic/VetClinicRecordInside';
import VetClinicRecordOutside from './pages/clinic/VetClinicRecordOutside';
import ClinicApprovals from './pages/clinic/ClinicApprovals';
import StallionForm from './pages/clinic/StallionForm';
import VetClinicRecordEdit from './pages/clinic/VetClinicRecordEdit';
import ReproductionProceduresList from './pages/clinic/ReproductionProceduresList';
import FarrierDetails from './pages/clinic/FarrierDetails';
import RecordDetails from './pages/clinic/RecordDetails';
import VetClinicForm from './pages/clinic/VetClinicForm';
import ProcedureEdit from './pages/clinic/ProcedureEdit';
import AddFarrierRecord from './pages/clinic/AddFarrierRecord';
import EditFarrierRecord from './pages/clinic/EditFarrierRecord';
import AddFarrierRecordInside from './pages/clinic/AddFarrierRecordInside';
import AddFarrierRecordOutside from './pages/clinic/AddFarrierRecordOutside';
import FarrierList from './pages/clinic/FarrierList';
import DirectInseminationForm from './pages/clinic/DirectInseminationForm';
import FarrierMedicationsList from './pages/clinic/FarrierMedicationsList';
import FarrierMedicationAdd from './pages/clinic/FarrierMedicationAdd';
import FarrierMedicationsEdit from './pages/clinic/FarrierMedicationsEdit';
import FarrierProcedureAdd from './pages/clinic/FarrierProcedureAdd';
import FarrierProceduresList from './pages/clinic/FarrierProceduresList';
import FarrierProcedureEdit from './pages/clinic/FarrierProcedureEdit';
import ReproductionPage from './pages/clinic/ReproductionPage';
import ReproductionRecordDetails from './pages/clinic/ReproductionRecordDetails';
import EditReproductionProcedure from './pages/clinic/EditReproductionProcedure';
import AddReproductionProcedure from './pages/clinic/AddReproductionProcedure';
import ClientsList from './pages/clients/ClientsList';
import HorsesClientsList from './pages/clients/HorsesClientsList';
import ChangePassword from './pages/shared/ChangePassword';
import NotFoundPage from './pages/shared/NotFoundPage';
import UserProfile from './pages/Profile';
import PrivateRoute from './components/PrivateRoute';
import Settings from './pages/admin/Settings';
import NoAccessPage from './pages/shared/NoAccessPage';
import RequirePermission from './components/RequirePermission';
import RolePermissions from './pages/admin/RolePermissions';
import UserPermissions from './pages/admin/UserPermissions';
import RolesPermissions from './pages/admin/RolesPermissions';
import AccessControlCenter from './pages/admin/AccessControlCenter';
import DatabaseBackup from './pages/admin/DatabaseBackup';
import ServicePricing from './pages/admin/ServicePricing';
import RestoreDeletedHorses from './pages/admin/RestoreDeletedHorses';
import CheckupTypesPage from './pages/clinic/CheckupTypesPage';
import HorseNFCPage from './pages/shared/HorseNFCPage';
import VetCheckupView from './pages/clinic/VetCheckupView';
import VetCheckupReport from './pages/clinic/VetCheckupReport';
import OwnerAlertTemplates from './pages/admin/OwnerAlertTemplates';
import { RouteGuardOutlet } from './routers/RouteGuard';
import MenuDesigner from './pages/admin/MenuDesigner';
import HorseFinanceCenter from './pages/accounting/HorseFinanceCenter';
import FinanceSummaryPage from './pages/accounting/FinanceSummaryPage';
import HorseLedgerInsidePage from './pages/accounting/HorseLedgerInsidePage';
// Lazy loading for better performance
import './App.css';

// Lazy loading for better performance
const Home = lazy(() => import('./pages/shared/Home'));
const Profile = lazy(() => import('./pages/Profile'));
// Lazy load other components similarly
/*function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem('token')
  );
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  const toggleSidebar = () => setSidebarExpanded((prev) => !prev);

  return (
    <Router>
      <AppLayout
        isAuthenticated={isAuthenticated}
        setIsAuthenticated={setIsAuthenticated}
        sidebarExpanded={sidebarExpanded}
        toggleSidebar={toggleSidebar}
      />
    </Router>
  );
}
*/
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem('token')
  );
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  const toggleSidebar = () => setSidebarExpanded((prev) => !prev);

  // Build a Data Router that routes everything to AppLayout.
  // This lets us enable React Router v7 future flags without refactoring your inner <Routes>.
  const router = React.useMemo(
    () =>
      createBrowserRouter(
        [
          {
            path: '/*', // send all paths to AppLayout which already contains <Routes>
            element: (
              <AppLayout
                isAuthenticated={isAuthenticated}
                setIsAuthenticated={setIsAuthenticated}
                sidebarExpanded={sidebarExpanded}
                toggleSidebar={toggleSidebar}
              />
            ),
          },
        ],
        {
          // Opt-in to v7 behavior to silence warnings
          future: {
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          },
        }
      ),
    [isAuthenticated, sidebarExpanded]
  );

  return (
    <RouterProvider
      router={router}
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    />
  );
}

function AppLayout({
  isAuthenticated,
  setIsAuthenticated,
  sidebarExpanded,
  toggleSidebar,
}) {
  const location = useLocation(); // ✅ هذا سيعمل الآن بشكل صحيح
  const { i18n } = useTranslation();
  const showAppChrome = isAuthenticated && location.pathname !== '/login';
  const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.dir = dir;
    document.body.dir = dir;
    document.documentElement.lang = i18n.language || 'en';
  }, [dir, i18n.language]);

  return (
    <Box
      className={sidebarExpanded ? 'sidebar-expanded' : 'sidebar-collapsed'}
      sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}
    >
      {/* Header, Sidebar */}
      {showAppChrome && (
        <>
          <Header
            sidebarExpanded={sidebarExpanded}
            toggleSidebar={toggleSidebar}
            setIsAuthenticated={setIsAuthenticated}
          />
          <Sidebar
            expanded={sidebarExpanded}
            toggleExpand={toggleSidebar}
            isAuthenticated={isAuthenticated}
            setIsAuthenticated={setIsAuthenticated}
          />
        </>
      )}

      {/* Main Content and Routes */}
      <Box
        className="app-main-content"
        dir={dir}
        sx={{
          flex: 1,
          padding: showAppChrome ? { xs: '16px', md: '20px' } : 0,
          marginLeft: showAppChrome ? (sidebarExpanded ? '250px' : '85px') : 0,
          boxSizing: 'border-box',
          transition: 'margin-left 0.3s ease',
        }}
      >
        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            {/* Route تسجيل الدخول */}
            <Route
              path="/login"
              element={<Login setIsAuthenticated={setIsAuthenticated} />}
            />

            {/* توجيه غير المسموحين إلى صفحة تسجيل الدخول */}
            <Route
              path="/"
              element={<Navigate to={isAuthenticated ? '/home' : '/login'} />}
            />

            {/* عرض صفحة "غير موجود" لجميع الروابط غير الصحيحة */}
            <Route path="*" element={<NotFoundPage />} />

            {/* Private Routes */}
            <Route element={<PrivateRoute isAuthenticated={isAuthenticated} />}>
              <Route path="/No-access" element={<NoAccessPage />} />
              {/* Gate every private route by menu/permissions */}
              <Route element={<RouteGuardOutlet />}>
                <Route
                  path="/home"
                  element={
                    <RequirePermission page="home">
                      <Home />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <RequirePermission page="profile">
                      <Profile />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/ChangePassword"
                  element={
                    <RequirePermission page="ChangePassword">
                      <ChangePassword />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/dashboard/*"
                  element={
                    <RequirePermission page="dashboard">
                      <Dashboard />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/register-horse"
                  element={
                    <RequirePermission page="register-horse">
                      <HorseRegistration />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/admin/menu-designer"
                  element={
                    <RequirePermission page="settings">
                      <MenuDesigner />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse-list"
                  element={
                    <RequirePermission page="horse-list">
                      <HorseList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/competitions"
                  element={
                    <RequirePermission page="competitions">
                      <HorseCompetitionsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse-edit/:horseId"
                  element={
                    <RequirePermission page="horse-edit">
                      <HorseEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse-details/:horseId"
                  element={
                    <RequirePermission page="horse-details">
                      <HorseDetails />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse-details-client/:horseId"
                  element={<HorseDetailsClient />}
                />
                <Route path="/my-horses" element={<ClientHorseList />} />
                <Route path="/my-horses/:horseId" element={<ClientHorseDetails />} />
                <Route path="/my-horse-stats" element={<ClientHorseStats />} />
                <Route
                  path="/horse-colors"
                  element={
                    <RequirePermission page="horse-colors">
                      <HorseColorList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse-breeds"
                  element={
                    <RequirePermission page="horse-breeds">
                      <HorseBreedList />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/CheckupTypesPage"
                  element={
                    <RequirePermission page="CheckupTypesPage">
                      <CheckupTypesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/RestoreDeletedHorses"
                  element={
                    <RequirePermission page="RestoreDeletedHorses">
                      <RestoreDeletedHorses />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/Statistics"
                  element={
                    <RequirePermission page="Statistics">
                      <Statistics />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/SystemSettings"
                  element={
                    <RequirePermission page="SystemSettings">
                      <SystemSettings />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/OwnerAlertTemplates"
                  element={
                    <RequirePermission page="OwnerAlertTemplates">
                      <OwnerAlertTemplates />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/AccountantReview"
                  element={
                    <RequirePermission page="AccountantReview">
                      <AccountantReview />
                    </RequirePermission>
                  }
                ></Route>
                <Route
                  path="/HorseTransportForm"
                  element={
                    <RequirePermission page="HorseTransportForm">
                      <HorseTransportForm />
                    </RequirePermission>
                  }
                ></Route>
                <Route
                  path="/HorseTransportReportPage"
                  element={
                    <RequirePermission page="HorseTransportReportPage">
                      <HorseTransportReportPage />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/HorseFinanceCenter"
                  element={
                    <RequirePermission page="HorseFinanceCenter">
                      <HorseFinanceCenter />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/PedigreeTree"
                  element={
                    <RequirePermission page="PedigreeTree">
                      <PedigreeTree />
                    </RequirePermission>
                  }
                />
                {/* Owners Management */}
                <Route
                  path="/owner-list"
                  element={
                    <RequirePermission page="owner-list">
                      <OwnerList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/OwnerHorsesList/:ownerId"
                  element={
                    <RequirePermission page="OwnerHorsesList">
                      <OwnerHorsesList />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/owner-edit/:ownerId"
                  element={
                    <RequirePermission page="owner-edit">
                      <OwnerEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/register-owner"
                  element={
                    <RequirePermission page="register-owner">
                      <OwnerRegistration />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/owner-details/:ownerId"
                  element={
                    <RequirePermission page="owner-details">
                      <OwnerDetails />
                    </RequirePermission>
                  }
                />
                {/* Stable and Rooms Management */}
                <Route
                  path="/stable-list"
                  element={
                    <RequirePermission page="stable-list">
                      <StableList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/stable-details/:stableId"
                  element={
                    <RequirePermission page="stable-details">
                      <StableDetails />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/stable-rooms/:stableId"
                  element={
                    <RequirePermission page="stable-rooms">
                      <StableRoomList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/room-details/:roomId"
                  element={
                    <RequirePermission page="room-details">
                      <StableRoomDetails />
                    </RequirePermission>
                  }
                />
                {/* Accommodations */}
                <Route
                  path="/accommodations"
                  element={
                    <RequirePermission page="accommodations">
                      <AccommodationList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/available-rooms"
                  element={
                    <RequirePermission page="available-rooms">
                      <AvailableRoomsView />
                    </RequirePermission>
                  }
                ></Route>
                <Route
                  path="/stable-room-list"
                  element={
                    <RequirePermission page="stable-room-list">
                      <StableRoomList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/stables/add"
                  element={
                    <RequirePermission page="stables/add">
                      <AddStable />
                    </RequirePermission>
                  }
                />
                {/* Users Management */}
                <Route
                  path="/userprofile"
                  element={
                    <RequirePermission page="userprofile">
                      <UserProfile />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/user-list"
                  element={
                    <RequirePermission page="user-list">
                      <UserList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/userregistration"
                  element={
                    <RequirePermission page="userregistration">
                      <UserRegistration />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/user-details/:userId"
                  element={
                    <RequirePermission page="user-details">
                      <UserDetails />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/useredit/:userId"
                  element={
                    <RequirePermission page="useredit">
                      <UserEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/RoleList"
                  element={
                    <RequirePermission page="RoleList">
                      <RoleList />
                    </RequirePermission>
                  }
                />
                {/* Clients Management */}
                <Route
                  path="/ClientsList"
                  element={
                    <RequirePermission page="ClientsList">
                      <ClientsList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/HorsesClientsList"
                  element={
                    <RequirePermission page="HorsesClientsList">
                      <HorsesClientsList />
                    </RequirePermission>
                  }
                />
                {/* Veterinary Clinic */}
                <Route
                  path="/VetClinicForm"
                  element={
                    <RequirePermission page="VetClinicForm">
                      <VetClinicForm />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/procedures"
                  element={
                    <RequirePermission page="procedures">
                      <ProceduresList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/procedure-add"
                  element={
                    <RequirePermission page="procedure-add">
                      <ProcedureAdd />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/ProcedureEdit/:procedureId"
                  element={
                    <RequirePermission page="ProcedureEdit">
                      <ProcedureEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/medications"
                  element={
                    <RequirePermission page="medications">
                      <MedicationsList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/medication-add"
                  element={
                    <RequirePermission page="medication-add">
                      <MedicationAdd />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/medication-edit/:medicationId"
                  element={
                    <RequirePermission page="medication-edit">
                      <MedicationsEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/VetClinicRecordInside"
                  element={
                    <RequirePermission page="VetClinicRecordInside">
                      <VetClinicRecordInside />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/VetClinicRecordOutside"
                  element={
                    <RequirePermission page="VetClinicRecordOutside">
                      <VetClinicRecordOutside />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/DirectInseminationForm"
                  element={
                    <RequirePermission page="DirectInseminationForm">
                      <DirectInseminationForm />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/StallionForm"
                  element={
                    <RequirePermission page="StallionForm">
                      <StallionForm />
                    </RequirePermission>
                  }
                />
                {/* Records and Farrier Management */}
                <Route
                  path="/RecordList"
                  element={
                    <RequirePermission page="RecordList">
                      <RecordList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/record-details/:record_id"
                  element={
                    <RequirePermission page="record-details">
                      <RecordDetails />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/AddFarrierRecord"
                  element={
                    <RequirePermission page="AddFarrierRecord">
                      <AddFarrierRecord />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/EditFarrierRecord/:record_id"
                  element={
                    <RequirePermission page="EditFarrierRecord">
                      <EditFarrierRecord />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/AddFarrierRecordInside"
                  element={
                    <RequirePermission page="AddFarrierRecordInside">
                      <AddFarrierRecordInside />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/AddFarrierRecordOutside"
                  element={
                    <RequirePermission page="AddFarrierRecordOutside">
                      <AddFarrierRecordOutside />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/FarrierList"
                  element={
                    <RequirePermission page="FarrierList">
                      <FarrierList />
                    </RequirePermission>
                  }
                />
                <Route path="/horse/:id" element={<HorseNFCPage />} />
                <Route path="/VetCheckupView" element={<VetCheckupView />} />
                <Route
                  path="/VetCheckupReport"
                  element={<VetCheckupReport />}
                />
                <Route
                  path="/VetClinicRecordEdit/:record_id"
                  element={
                    <RequirePermission page="VetClinicRecordEdit">
                      <VetClinicRecordEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/FarrierDetails/:record_id"
                  element={
                    <RequirePermission page="FarrierDetails">
                      <FarrierDetails />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/farrier-medication-add"
                  element={
                    <RequirePermission page="farrier-medication-add">
                      <FarrierMedicationAdd />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/farrier-medications"
                  element={
                    <RequirePermission page="farrier-medications">
                      <FarrierMedicationsList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/farrier-medication-edit/:medicationId"
                  element={
                    <RequirePermission page="farrier-medication-edit">
                      <FarrierMedicationsEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/farrier-procedure-add"
                  element={
                    <RequirePermission page="farrier-procedure-add">
                      <FarrierProcedureAdd />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/farrier-procedures"
                  element={
                    <RequirePermission page="farrier-procedures">
                      <FarrierProceduresList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/farrier-procedure-edit/:procedureId"
                  element={
                    <RequirePermission page="farrier-procedure-edit">
                      <FarrierProcedureEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/HorseHistory/:horseId"
                  element={
                    <RequirePermission page="HorseHistory">
                      <HorseHistory />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/ClinicApprovals"
                  element={
                    <RequirePermission page="ClinicApprovals">
                      <ClinicApprovals />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/ReproductionPage"
                  element={
                    <RequirePermission page="ReproductionPage">
                      <ReproductionPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/reproduction-details/:id"
                  element={
                    <RequirePermission page="ReproductionRecordDetails">
                      <ReproductionRecordDetails />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/ReproductionProceduresList"
                  element={
                    <RequirePermission page="ReproductionProceduresList">
                      <ReproductionProceduresList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/EditReproductionProcedure/:id"
                  element={
                    <RequirePermission page="EditReproductionProcedure">
                      <EditReproductionProcedure />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/ExpensesPage"
                  element={
                    <RequirePermission page="ExpensesPage">
                      <ExpensesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/financesummary"
                  element={
                    <RequirePermission page="/financesummary">
                      <FinanceSummaryPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/HorseLedgerInsidePage"
                  element={
                    <RequirePermission page="/HorseLedgerInsidePage">
                      <HorseLedgerInsidePage />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/AddReproductionProcedure"
                  element={
                    <RequirePermission page="AddReproductionProcedure">
                      <AddReproductionProcedure />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <RequirePermission page="settings">
                      <Settings />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/settings/access-control"
                  element={
                    <RequirePermission page="access-control">
                      <AccessControlCenter />
                    </RequirePermission>
                  }
                />
                <Route path="/access-control" element={<Navigate to="/settings/access-control" replace />} />
                <Route
                  path="/settings/backups"
                  element={
                    <RequirePermission page="settings/backups">
                      <DatabaseBackup />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/settings/pricing"
                  element={
                    <RequirePermission page="settings/pricing">
                      <ServicePricing />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/role-permissions"
                  element={
                    <RequirePermission page="role-permissions">
                      <RolePermissions />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/roles-permissions"
                  element={
                    <RequirePermission page="roles-permissions">
                      <RolesPermissions />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/UserPermissions"
                  element={
                    <RequirePermission page="UserPermissions">
                      <UserPermissions />
                    </RequirePermission>
                  }
                />
                {/* Add other protected routes here */}
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </Box>

      {/* Footer */}
      {isAuthenticated && location.pathname !== '/login' && (
        <Footer sidebarExpanded={sidebarExpanded} />
      )}
    </Box>
  );
}

export default App;
